// The guided tour for the Commons anonymous demo.
//
// Product-orientation arc, with every route change grounded in a real
// Commons-native control so the visitor sees the door before walking
// through it:
//
//   Question → Six categories → tap category door → Library
//     → tap project card → Project Detail → I'm in
//     → tap Roots pill → Roots
//     → return home → tap You ✨ → Profile → Start a project
//     → Home finale
//
// Steps flagged `interactive: true` wait for the visitor to tap the
// halo target itself (an actual Commons control). The visitor's own
// tap performs the real navigation; the tour advances as a side
// effect. `route()` is still declared for those steps as a safety
// net in case the persisted step is loaded on a mismatched URL.

import {
  effectiveStatus,
  personIsIn,
  totalPeopleCommitted,
  totalPeopleNeeded,
  thingRequirements,
  pledgedForReq,
  peopleRequirements,
} from "../lib/domain";

// -------- helpers --------

// Sticky per-tour selection: chosen once (typically at the "possibilities"
// stop when we derive the category tile), then reused for library,
// Project Detail, commitment, Roots pill, and Roots.
let stickyProjectId = null;
export function resetTourStickyState() {
  stickyProjectId = null;
}
export function getStickyProjectId() {
  return stickyProjectId;
}
export function restoreTourStickyState(id) {
  if (typeof id === "string" && id.length > 0) stickyProjectId = id;
}

function tourProject(data, user) {
  if (stickyProjectId) {
    const p = data.projects.find((pp) => pp.id === stickyProjectId);
    if (p && effectiveStatus(p, data.pledges) === "gathering") return p;
    stickyProjectId = null;
  }
  const gathering = data.projects.filter(
    (p) => effectiveStatus(p, data.pledges) === "gathering"
  );
  if (!gathering.length) return null;

  // Mar 2026 participation invariant: the tour's "I'm in" moment must
  // land on a project the visitor is genuinely authorised to join.
  // Founders bypass; everyone else needs an existing invitation record
  // (pending is ideal; accepted also works because the visitor may not
  // yet have pledged to a People role on that project).
  const eligible = gathering.filter((p) => {
    if (p.stewardId === user.personId) return true;
    return (data.invitations || []).some(
      (i) => i.projectId === p.id && i.inviteePersonId === user.personId
    );
  });
  const pool = eligible.length ? eligible : gathering;

  const scored = pool.map((p) => {
    const peopleReqs = peopleRequirements(p);
    const thingReqs = thingRequirements(p);
    const openPeopleReq = peopleReqs.find(
      (r) => pledgedForReq(r.id, data.pledges) < r.needed
    );
    const openThingReq = thingReqs.find(
      (r) => pledgedForReq(r.id, data.pledges) < r.needed
    );
    const committed = totalPeopleCommitted(p, data.pledges);
    const needed = totalPeopleNeeded(p);
    const inIt = personIsIn(user.personId, p.id, data.pledges);
    const traction = needed > 0 ? committed / needed : 0;
    let score = 0;
    if (openPeopleReq && !inIt) score += 100;
    if (openThingReq) score += 30;
    if (traction >= 0.2 && traction <= 0.9) score += 25;
    if (traction > 0.9) score += 5;
    if (needed >= 20) score += 5;
    return { p, score };
  });
  scored.sort((a, b) => b.score - a.score);
  const chosen = scored[0]?.p || pool[0] || gathering[0];
  if (chosen) stickyProjectId = chosen.id;
  return chosen;
}

// -------- the tour --------

export const STEPS = [
  {
    id: "question",
    title: "What would you do if enough people would help?",
    body: "Commons is a place to find out.",
    route: () => "/",
    targetSelector: () => '[data-tour="home-thesis"]',
    scroll: "target",
  },

  {
    id: "possibilities",
    title: "Start with what you want to make happen.",
    body:
      "A gathering. A neighborhood improvement. Something you want to change, make, care for, or try. Tap this category to open it.",
    route: () => "/",
    // Halo narrows from the whole grid to the ONE specific category
    // tile whose library contains the sticky tour project. The
    // visitor's tap performs the real navigation.
    targetSelector: ({ data, user }) => {
      const p = tourProject(data, user);
      return p ? `[data-testid="cat-tile-${p.category}"]` : '[data-tour="category-grid"]';
    },
    scroll: "target",
    interactive: true,
    fallbackCta: "Skip this step",
  },

  {
    id: "library",
    title: "See what people are trying.",
    body:
      "Each library is full of projects people are trying to make possible. Tap the highlighted project to open it.",
    route: ({ data, user }) => {
      const p = tourProject(data, user);
      return p ? `/commons/${p.category}` : "/";
    },
    targetSelector: ({ data, user }) => {
      const p = tourProject(data, user);
      return p ? `[data-testid="project-card-${p.id}"]` : '[data-tour="library-projects"]';
    },
    scroll: "target",
    interactive: true,
    fallbackCta: "Skip this step",
  },

  {
    id: "project",
    title: "What would it take?",
    body:
      "Every project says what it needs: people to fill the roles, and things people can bring. It only happens when enough of both are covered.",
    route: ({ data, user }) => {
      const p = tourProject(data, user);
      return p ? `/project/${p.id}` : "/";
    },
    targetSelector: () => '[data-tour="readiness"]',
    scroll: "target",
  },

  {
    id: "try-it",
    title: "Be one of the people who makes it possible.",
    body:
      "Pick a role and tap Join. You're saying: count me in if this project gets what it needs.",
    route: ({ data, user }) => {
      const p = tourProject(data, user);
      return p ? `/project/${p.id}` : "/";
    },
    targetSelector: () => '[data-tour="volunteer-role"]',
    scroll: "target",
    fallbackCta: "I'll commit later",
    // Advance is driven by markCommitmentMade() from ProjectDetail's
    // pledge success path — a real POST /pledge must succeed before
    // we move on. See TourProvider.
  },

  {
    id: "roots-pill",
    title: "Now see how they gathered.",
    body:
      "Tap Roots to see how the people around this project found each other.",
    // Stay on the same project detail page after the pledge — no
    // teleport. The visitor's own tap on the Roots pill performs the
    // navigation to /roots/:id, and this stop advances on that click.
    route: ({ data, user }) => {
      const p = tourProject(data, user);
      return p ? `/project/${p.id}` : "/";
    },
    targetSelector: () => '[data-testid="roots-inline-pill"]',
    scroll: "target",
    interactive: true,
    fallbackCta: "Skip this step",
  },

  {
    id: "roots",
    title: "See how people found each other.",
    body:
      "Roots shows how the people around a project gathered, branch by branch.",
    route: ({ data, user }) => {
      const p = tourProject(data, user);
      return p ? `/roots/${p.id}` : "/";
    },
    targetSelector: () => '[data-tour="roots-canvas"]',
    scroll: "none",
    minCardOffset: 60,
  },

  {
    id: "return-home",
    title: "Back to you.",
    body:
      "Tap You ✨ to see the projects you're part of.",
    // Guided return to Home so the visitor can see the real doorway
    // to Profile. The tap on You ✨ performs the real navigation.
    route: () => "/",
    targetSelector: () => '[data-testid="home-profile-btn"]',
    scroll: "top",
    interactive: true,
    fallbackCta: "Skip this step",
  },

  {
    id: "you",
    title: "See what you're part of.",
    body:
      "You keeps the projects you're stewarding, the projects you've joined, and what you've already tried in one place.",
    route: () => "/profile",
    targetSelector: () =>
      document.querySelector('[data-tour="profile-joined"]')
        ? '[data-tour="profile-joined"]'
        : '[data-tour="profile-participation"]',
    scroll: "target",
  },

  {
    id: "start-something",
    title: "Or start something of your own.",
    body:
      "Commons isn't only for joining other people's projects. Start with something you wish existed and figure out what it would take.",
    route: () => "/profile",
    targetSelector: () => '[data-tour="profile-start-project"]',
    scroll: "target",
  },

  {
    id: "finale",
    title: "So, what would you do?",
    body:
      "What could you make happen if you could first find out whether enough people would help?",
    route: () => "/",
    targetSelector: () => null,
    scroll: "top",
    isFinale: true,
    primaryCta: "Start exploring",
    secondaryCta: "Restart with a fresh demo",
  },
];

// Best-guess fallback for stops whose target is missing — returns the next
// stop index whose target is likely to resolve. Called by TourProvider.
export function nextResolvableIndex(steps, fromIndex) {
  return Math.min(fromIndex + 1, steps.length - 1);
}
